import React, { useEffect, useState, useMemo } from "react";
import {
  APP_VIEW,
  CALCULATION_RECORD_COMMENTS_STRUCTURE,
  CALCULATION_RECORD_DETAILS_STRUCTURE,
  DETAILS_FIELD_FORMATTING,
  otherThanJDWINCountries,
} from "../../constants/constant";
import { Grid } from "@mui/material";
import { getUserRole } from "../../utils/utils";
import Field from "../../common/field";
import { useTranslation } from "../../common/translation-context";
import dayjs from "dayjs";
import { useSelector } from "react-redux";

function LineItemDetailPanel(props) {
  const [editedvalues, setEditedvalues] = useState({});
  const [isInEditMode, setIsInEditMode] = useState(false);
  const { editableFields } = props;
  const userData = useSelector((state) => state.userState.userData);
  const { translate: t } = useTranslation();
  const [recordData, setRecordData] = useState({});

  const generateEditedValuesSet = useMemo(() => {
    return Object.keys(editableFields).reduce((obj, fields) => {
      obj[fields] = props.row[fields];
      return obj;
    }, {});
  }, [editableFields, props.row]);

  useEffect(() => {
    setEditedvalues(generateEditedValuesSet);
    setRecordData(props.row);
    if (
      (props.view === APP_VIEW.BP_CHECK ||
        props.view === APP_VIEW.INCLUDE ||
        props.view === APP_VIEW.EXCLUDE) &&
      getUserRole(userData) === "BP"
    ) {
      setIsInEditMode(true);
    }
  }, [props.view, props.row, generateEditedValuesSet, userData]);

  const handleSaveClick = () => {
    const updatedRecordData = {
      ...recordData,
      id: recordData.id,
      calculatedInterest: editedvalues.calculatedInterest,
      noOfDays: editedvalues.noOfDays,
      startDate: dayjs(editedvalues.startDate).isValid()
        ? dayjs(editedvalues.startDate).format("YYYY-MM-DD")
        : null,
      endDate: dayjs(editedvalues.endDate).isValid()
        ? dayjs(editedvalues.endDate).format("YYYY-MM-DD")
        : null,
      previousReasonCode: editedvalues.previousReasonCode,
      interestPercentage: editedvalues.interestPercentage,
    };
    props.save(updatedRecordData);
  };

  const handleInputChange = (field, value, props, event) => {
    let latest = { ...editedvalues, [field]: value };
    setEditedvalues(latest);

    if (field === "previousReasonCode") {
      latest.previousReasonCode = value;
      let selectedReasonCode;
      if (otherThanJDWINCountries.includes(recordData.country)) {
        // For non-JD countries, find by reasonCode only
        selectedReasonCode = props.reasonCodeRateList.find(
          (item) => item.reasonCode === value,
        );
      } else {
        // For JD country, find by reasonCode AND currency
        selectedReasonCode = props.reasonCodeRateList.find(
          (item) =>
            item.reasonCode === value &&
            item.currency === recordData.currencyKey,
        );
      }

      latest.interestPercentage = selectedReasonCode
        ? selectedReasonCode.rate
        : null;
    }

    if (latest.startDate && latest.endDate) {
      setRecordData((prevRecordData) => ({
        ...prevRecordData,
        startDate: latest.startDate,
        endDate: latest.endDate,
      }));
      updateDependentFields(latest);
    }

    if (event && (event.key === "Enter" || event.key === "Tab")) {
      handleSaveClick();
    }
  };

  const updateDependentFields = (latest) => {
    if (
      latest.startDate &&
      latest.endDate &&
      (recordData.interestPercentage || latest.interestPercentage)
    ) {
      const interestPercentage =
        recordData.interestPercentage || latest.interestPercentage;

      latest.noOfDays = dayjs(latest.endDate).diff(
        dayjs(latest.startDate),
        "days",
      );

      if (
        interestPercentage &&
        recordData.amountInDocCurrency &&
        latest.noOfDays > 0
      ) {
        let interest =
          (recordData.amountInDocCurrency *
            interestPercentage *
            latest.noOfDays) /
          36500;

        if (recordData.debitCreditIndicator === "H") {
          latest.calculatedInterest = -interest.toFixed(2);
        } else {
          latest.calculatedInterest = Math.max(interest, 0).toFixed(2);
        }

        if (latest.startDate < latest.endDate) {
          props.updateTotalInterest(
            recordData.id,
            latest.calculatedInterest,
            recordData.assignmentNo,
            recordData.customerNo,
          );
        }
      } else {
        latest.calculatedInterest = 0;
        latest.noOfDays = 0;
      }
      setEditedvalues(latest);
    } else if (latest.interestPercentage) {
      setEditedvalues(latest);
    }
  };

  const isValidDate = (item) => {
    const dates = [
      "startDate",
      "endDate",
      "docDateInDoc",
      "clearingDate",
      "dueDateOfInvoice",
    ];
    return dates.includes(item);
  };

  const getFieldOrData = (item) => {
    if ((item === "startDate" || item === "endDate") && isInEditMode) {
      return (
        <Field
          key={item}
          item={item}
          recordData={recordData}
          onChange={(value, event) =>
            handleInputChange(item, value, null, event)
          }
          attr={editableFields[item]}
          type={editableFields[item].type}
          value={editedvalues[item]}
          endPlaceHolder={
            DETAILS_FIELD_FORMATTING.hasOwnProperty(item) &&
            DETAILS_FIELD_FORMATTING[item].type === "custom"
              ? DETAILS_FIELD_FORMATTING[item].value
              : props.row.currencyKey
          }
        />
      );
    }

    if (
      item === "previousReasonCode" &&
      props.reasonCodeRateList &&
      props.reasonCodeRateList.length > 0 &&
      isInEditMode &&
      (!props.row["reasonCodeForPayment"] ||
        (!props.row["reasonCodeForPayment"].startsWith("F") &&
          !props.row["reasonCodeForPayment"].startsWith("FS")))
    ) {
      let reasonCodeOptions;
      if (otherThanJDWINCountries.includes(props.row.country)) {
        // For non-JD countries
        reasonCodeOptions = props.reasonCodeRateList.map(
          (item) => item.reasonCode,
        );
      } else {
        // For JD country, filter by currency
        const filteredReasonCodes = props.row.currencyKey
          ? props.reasonCodeRateList.filter(
              (item) => item.currency === props.row.currencyKey,
            )
          : props.reasonCodeRateList;

        reasonCodeOptions = filteredReasonCodes.map((item) => item.reasonCode);
      }

      reasonCodeOptions.sort((a, b) => {
        const getPrefixAndNumber = (code) => {
          const match = code.match(/^([A-Za-z]+)(\d+)$/);
          if (match) {
            return { prefix: match[1], number: parseInt(match[2], 10) };
          }
          return { prefix: code, number: 0 };
        };

        const { prefix: prefixA, number: numberA } = getPrefixAndNumber(a);
        const { prefix: prefixB, number: numberB } = getPrefixAndNumber(b);

        if (prefixA === prefixB) {
          return numberA - numberB;
        } else {
          return prefixA.localeCompare(prefixB);
        }
      });

      return (
        <Field
          key={item}
          item={item}
          recordData={recordData}
          onChange={(value) => handleInputChange(item, value, props)}
          attr={editableFields[item]}
          type={editableFields[item].type}
          value={editedvalues[item]}
          options={reasonCodeOptions}
        />
      );
    }

    if (isValidDate(item) && props.row[item]) {
      return (
        <span style={{ width: "100%" }}>
          {dayjs(props.row[item]).format("DD-MM-YYYY")}
        </span>
      );
    }
    const displayValue =
      editedvalues[item] || editedvalues[item] === 0
        ? editedvalues[item]
        : props.row[item];
    return (
      <span style={{ width: "100%" }}>
        {displayValue}
        {item === "calculatedInterest" &&
        (displayValue || displayValue === 0) &&
        props.row.currencyKey
          ? ` ${props.row.currencyKey}`
          : ""}
      </span>
    );
  };

  const generateDetailsSection = (fields) => {
    return (
      <Grid
        key={JSON.stringify(fields)}
        xs={4}
        style={{ display: "flex", flexDirection: "column", width: "100%" }}
      >
        {fields.map((item) => {
          const value = getFieldOrData(item);

          if (
            (item === "cancelledDebitNoteNumber" ||
              item === "creditNoteNumber") &&
            (props.row[item] === null || props.row[item] === "")
          ) {
            return null;
          }

          return (item === "machineExceptionComments" &&
            props.view === "bp_check") ||
            item !== "machineExceptionComments" ? (
            <div key={item} style={{ display: "flex", padding: "5px 0" }}>
              <span style={{ width: "30%", marginRight: "5px" }}>
                {t(item)}
              </span>
              <div
                style={{
                  width: "80%",
                  marginRight: "10px",
                  wordWrap: "break-word",
                  overflow: "hidden",
                }}
              >
                {value}
              </div>
            </div>
          ) : null;
        })}
      </Grid>
    );
  };

  const generateCommentsSection = (field) => {
    return (
      <div
        key={field}
        style={{
          display: "flex",
          flexDirection: "row",
          marginTop: "10px",
          width: "100%",
        }}
      >
        <span style={{ width: "200px" }}>{t(field)}</span>
        {getFieldOrData(field)}
      </div>
    );
  };

  const generateDetails = () => {
    return (
      <div style={{ width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "row", width: "100%" }}>
          {CALCULATION_RECORD_DETAILS_STRUCTURE.map((field) =>
            generateDetailsSection(field),
          )}
        </div>
        {CALCULATION_RECORD_COMMENTS_STRUCTURE.map((field) =>
          generateCommentsSection(field),
        )}
      </div>
    );
  };

  return (
    <div style={{ background: "#FFF" }}>
      <div
        className="details-panel"
        style={{
          marginLeft: "30px",
          marginRight: "5px",
          border: "1px solid rgba(0,0,0,0.1)",
        }}
      >
        <Grid container spacing={1}>
          <Grid
            xs={12}
            className="main"
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              gap: "50px",
              padding: "10px",
              background: "#FFF",
            }}
          >
            {generateDetails()}
          </Grid>
        </Grid>
      </div>
    </div>
  );
}

export default LineItemDetailPanel;
